import { LocationScope, LocationScopeKind, UserRole } from '../../roles/types/enums';

import type { PayloadUserData } from './users';

/**
 * The access breadth a principal has, resolved from its role and location scope.
 *
 * Every read path narrows by exactly one of these. Producing it in one place keeps
 * the "absent means LIST" rule from being re-implemented — and mis-implemented — at
 * each of the call sites that scope a query.
 */
export type ResolvedLocationScope =
  /** Platform operator: no tenant restriction at all. */
  | { kind: LocationScopeKind.ALL_TENANTS }
  /** Company-wide: pin the company, do not pin a location. */
  | { kind: LocationScopeKind.COMPANY; companyId: number }
  /** One active location at a time: pin both. */
  | { kind: LocationScopeKind.LOCATION; companyId: number; locationId: number };

/**
 * Roles whose breadth is company-wide by definition. Their `locationScope` is forced
 * to {@link LocationScope.ALL} on write, and read paths must not pin them to a
 * location even if one happens to be set.
 */
const COMPANY_WIDE_ROLES: readonly UserRole[] = [UserRole.COMPANY_ROOT, UserRole.COMPANY_ADMIN];

/**
 * Resolves how far a principal can see.
 *
 * Call this instead of branching on `user.role` directly. A site that still compares
 * roles by hand will not learn about a new company-wide role, and the failure is
 * silent — it narrows to the active location rather than erroring.
 *
 * Fail-closed by construction: an absent `locationScope` resolves to `LIST`, which is
 * the narrowest option, so anything produced before this field existed cannot widen.
 */
export function resolveLocationScope(user: PayloadUserData): ResolvedLocationScope {
  if (user.role === UserRole.SUPER_ADMIN) return { kind: LocationScopeKind.ALL_TENANTS };

  const isCompanyWide = user.locationScope === LocationScope.ALL || COMPANY_WIDE_ROLES.includes(user.role);
  if (isCompanyWide) return { kind: LocationScopeKind.COMPANY, companyId: user.companyId };

  return { kind: LocationScopeKind.LOCATION, companyId: user.companyId, locationId: user.locationId };
}

/**
 * The locations a principal holds, as a list.
 *
 * `locationIds` is the granted set and `locationId` the one currently active. A caller
 * that reads only the active one silently narrows a multi-location user to wherever they
 * happen to be standing, which is right for operational data and wrong for administration
 * — a manager of two locations administers both without switching back and forth.
 *
 * Empty for a company-wide principal: their breadth is a rule over every location of the
 * company, not a set, so pinning them to a list would narrow rather than widen.
 */
export function grantedLocationIds(user: PayloadUserData): number[] {
  const ids = user.locationIds?.length ? user.locationIds : [user.locationId];

  return ids.filter((id): id is number => typeof id === 'number');
}

/**
 * Whether `locationId` is one the principal may act on — creating a record there,
 * filtering by it, or switching to it.
 *
 * Distinct from {@link resolveLocationScope}: that one narrows a read, this one
 * validates a target the caller supplied. The gateway performs no such check today,
 * which is harmless only while every non-root user has exactly one location.
 *
 * `COMPANY` scope needs the company's live location ids, which this module cannot
 * reach; pass them in, or let the caller assert the company match separately.
 */
export function canAccessLocation(
  user: PayloadUserData,
  locationId: number,
  companyLocationIds?: readonly number[],
): boolean {
  const scope = resolveLocationScope(user);

  const checks: Record<LocationScopeKind, () => boolean> = {
    [LocationScopeKind.ALL_TENANTS]: () => true,
    [LocationScopeKind.COMPANY]: () => (companyLocationIds ? companyLocationIds.includes(locationId) : true),
    [LocationScopeKind.LOCATION]: () => grantedLocationIds(user).includes(locationId),
  };

  // Unknown kinds cannot occur while the union is exhaustive, but denying rather than
  // defaulting to true keeps a future member from silently granting access.
  return checks[scope.kind]?.() ?? false;
}
